<script lang="ts">
import { PRICING_EN } from "$lib/i18n/copy/pricing";
import BilledOnlyCallout from "$lib/pricing/BilledOnlyCallout.svelte";
import {
  BILLED_FLAG_NOTE,
  BILLED_NEVER,
  BILLED_RULE,
  BILLING_OUTCOME_ORDER,
  BILLING_OUTCOMES,
  FREE_START_REGISTERED,
  FREE_START_SANDBOX,
  FREE_START_TOPUP,
  PRICE_FLAT_NOTE,
  PRICE_PER_DOCUMENT,
} from "$lib/pricing/copy";
import { jsonLdScript, pricingGraph } from "$lib/seo/jsonld";
import SeoHead from "$lib/seo/SeoHead.svelte";
import SiteFooter from "$lib/seo/SiteFooter.svelte";
import { DOCS_URL } from "$lib/seo/site";
import type { PageData } from "./$types";

// THE ONE THING AN EXPERIMENT MAY CHANGE ON THIS PAGE, and it is deliberately
// the headline and nothing else: a test that moved the price, the billing rule
// or a control would be a test that showed two visitors two different products.
// The variant arrives already chosen, in the first HTML, so there is nothing to
// swap after hydration and nothing to flash.
//
// `control` is the answer on every build with no experiment running, for every
// visitor who did not allow one, and on every deployment with no platform — so
// the default below is the page as it has always been.
let { data }: { data: PageData } = $props();
const variant = $derived(data.experiment?.variant ?? "control");
const heading = $derived(variant === "treatment" ? "Pay only for what we recognise" : "Pricing");

const title = PRICING_EN.title;
const description = PRICING_EN.description;

// The product and its offer, declared for a crawler that has no other way to
// bind a two-word brand to the thing it sells ($lib/seo/jsonld).
const jsonLd = jsonLdScript(pricingGraph());
</script>

<SeoHead {title} {description} path="/pricing" {jsonLd} />

<div class="bg-slate-50">
  <main class="mx-auto max-w-6xl px-6 py-12">
    <!-- The number first, on its own, because it is the whole offer. -->
    <section aria-labelledby="price">
      <h1
        id="price"
        class="text-4xl font-semibold tracking-tight sm:text-5xl"
        data-testid="price-heading"
        data-variant={variant}>{heading}</h1>
      <p class="mt-4 flex flex-wrap items-baseline gap-3">
        <span class="text-6xl font-semibold tracking-tight" data-testid="price-amount"
          >{PRICE_PER_DOCUMENT}</span
        >
        <span class="text-xl text-slate-600">per document</span>
      </p>
      <p class="mt-3 max-w-2xl text-lg text-slate-600" data-testid="price-flat">{PRICE_FLAT_NOTE}</p>
      <p class="mt-3 max-w-2xl text-lg font-semibold text-emerald-700" data-testid="price-free">
        {PRICING_EN.freePlan}
      </p>
      <div class="mt-8 max-w-3xl">
        <BilledOnlyCallout />
      </div>
      <div class="mt-8 flex flex-wrap gap-3">
        <a
          href="/register"
          data-analytics-id="pricing.register.click"
          class="rounded-md bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700"
        >
          Get your key
        </a>
        <a
          href="/#demo"
          data-analytics-id="pricing.demo.click"
          class="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
        >
          Try the live demo
        </a>
      </div>
    </section>

    <!-- What the charge actually turns on, stated as the rule rather than left
         to the reader's idea of "recognised". -->
    <section class="mt-14" aria-labelledby="how-billing-works">
      <h2 id="how-billing-works" class="text-2xl font-semibold tracking-tight">
        How billing works
      </h2>
      <p class="mt-3 max-w-3xl text-slate-600">{BILLED_RULE}</p>
      <p class="mt-3 max-w-3xl text-slate-600">{BILLED_NEVER}</p>
      <p class="mt-3 max-w-3xl text-slate-600">{BILLED_FLAG_NOTE}</p>

      <div class="mt-6 scroll-x rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table class="min-w-full divide-y divide-slate-200 text-sm" data-testid="billing-outcomes">
          <thead class="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th class="px-4 py-3 font-medium">Result</th>
              <th class="px-4 py-3 font-medium">What it means</th>
              <th class="px-4 py-3 font-medium">Cost</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            {#each BILLING_OUTCOME_ORDER as status (status)}
              {@const outcome = BILLING_OUTCOMES[status]}
              <tr>
                <td class="px-4 py-3 font-medium text-slate-900">{outcome.label}</td>
                <td class="px-4 py-3 text-slate-600">{outcome.meaning}</td>
                <td class="px-4 py-3 font-semibold whitespace-nowrap">
                  {#if outcome.billed}
                    <span class="text-slate-900">{PRICE_PER_DOCUMENT}</span>
                  {:else}
                    <span class="text-emerald-700">Free</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </section>

    <!-- Where the free recognition sits, so "start free" is a number rather
         than a mood. -->
    <section class="mt-14" aria-labelledby="starting-out">
      <h2 id="starting-out" class="text-2xl font-semibold tracking-tight">Starting out</h2>
      <ul class="mt-4 grid max-w-4xl gap-4 sm:grid-cols-3">
        <li class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p class="text-sm font-semibold text-slate-900">No account</p>
          <p class="mt-2 text-sm text-slate-600">{FREE_START_SANDBOX}</p>
        </li>
        <li class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p class="text-sm font-semibold text-slate-900">After you register</p>
          <p class="mt-2 text-sm text-slate-600">{FREE_START_REGISTERED}</p>
        </li>
        <li class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p class="text-sm font-semibold text-slate-900">Then</p>
          <p class="mt-2 text-sm text-slate-600">{FREE_START_TOPUP}</p>
        </li>
      </ul>
      <p class="mt-6 max-w-3xl text-slate-600">
        Everything else is included at the same price: history retention you choose, the portrait crop,
        MRZ check digits, the cross-checks between the machine-readable zone and the visual zone, and
        the full field set. See the
        <a href={DOCS_URL} rel="external" class="underline hover:text-slate-900">documentation</a>
        for the request options and the response shape.
      </p>
      <p class="prose-links mt-4 max-w-3xl text-slate-600">{@html PRICING_EN.compareHtml}</p>
    </section>
  </main>

  <SiteFooter />
</div>
